import { useMemo } from 'react';
import type { DataEntry } from '../types';
import formattedCentsAmount from '../formattedCentsAmount';

const normalizeString = (str?: string) => String(str).trim().toLowerCase();

const isSearchStringSet = (search?: string) => typeof search === 'string' && search.trim() !== '';

const handleNumericSearch = (searchString: string) => {
  const amountMatch = searchString.match(/^([<>]=?|=)\s*(-?\d+(?:\.\d+)?)(?:-(-?\d+(?:\.\d+)?))?/);
  if (!amountMatch) return null;

  const operator = amountMatch[1];
  const value = parseFloat(amountMatch[2]);
  const hasRange = typeof amountMatch[3] !== 'undefined';
  const rangeEnd = hasRange ? parseFloat(amountMatch[3]) : undefined;

  const getAmount = dataEntry => dataEntry.amountInCents / 100;

  if (operator === '=') {
    if (hasRange) {
      const min = value;
      const max = rangeEnd !== undefined ? rangeEnd : min;
      const [low, high] = min <= max ? [min, max] : [max, min];
      return dataEntry => {
        const amount = getAmount(dataEntry);
        return amount >= low && amount <= high;
      };
    }
    return dataEntry => getAmount(dataEntry) === value;
  }
  if (operator === '<') return dataEntry => getAmount(dataEntry) < value;
  if (operator === '>') return dataEntry => getAmount(dataEntry) > value;
  if (operator === '<=') return dataEntry => getAmount(dataEntry) <= value;
  if (operator === '>=') return dataEntry => getAmount(dataEntry) >= value;

  return null;
};

const handleStringSearches = (dataEntries: DataEntry[], search?: string) => {
  if (!isSearchStringSet(search)) {
    return dataEntries; // No search term, return all entries
  }
  const searchString = normalizeString(search);

  return dataEntries.filter(dataEntry => {
    const {
      amountInCents,
      journalEntry: { clientCatResponse, clientComments, internalNotes, vendorName },
      ledgerAccount,
    } = dataEntry;
    const decimalStringAmount = (dataEntry.amountInCents / 100).toFixed(2);
    const dollarAmountString = `$${formattedCentsAmount(dataEntry.amountInCents)}`;

    // Check vendor name
    if (normalizeString(vendorName).includes(searchString)) {
      return true;
    }
    // Check client categorization response
    if (normalizeString(clientCatResponse).includes(searchString)) {
      return true;
    }
    // Check internal notes
    if (internalNotes.some(({ body }) => normalizeString(body).includes(searchString))) {
      return true;
    }
    // Check client comments
    if (clientComments.some(({ body }) => normalizeString(body).includes(searchString))) {
      return true;
    }
    // Check amounts
    if (String(amountInCents).includes(searchString)) {
      return true;
    }
    if (decimalStringAmount.includes(searchString)) {
      return true;
    }
    if (dollarAmountString.includes(searchString)) {
      return true;
    }
    return false;
  });
};

/**
 * Custom hook for handling search functionality in the General Ledger
 *
 * @param dataEntries - Array of data entries to search through
 * @param search - Search term (string or undefined)
 * @returns Object containing filtered data entries and search metadata
 */
export const useSearchResults = (dataEntries: DataEntry[], search: string | undefined) => {
  const searchResults = useMemo(() => {
    if (!isSearchStringSet(search)) {
      return {
        filteredEntries: dataEntries,
        searchType: 'none' as const,
        matchingEntryIds: undefined,
      };
    }

    const searchString = normalizeString(search);

    const numericFilter = handleNumericSearch(searchString);
    if (numericFilter) {
      const filteredEntries = dataEntries.filter(numericFilter);
      return {
        filteredEntries,
        searchType: 'numeric' as const,
        matchingEntryIds: new Set(filteredEntries.map(entry => entry.id)),
      };
    }

    const filteredEntries = handleStringSearches(dataEntries, search);
    return {
      filteredEntries,
      searchType: 'string' as const,
      matchingEntryIds: new Set(filteredEntries.map(entry => entry.id)),
    };
  }, [dataEntries, search]);

  return {
    ...searchResults,
    isSearchActive: isSearchStringSet(search),
  };
};

export default useSearchResults;
